/**
 * @fileoverview Experiment registry — hypotheses, metrics, and lifecycle metadata.
 * @description Code-side source of truth. Flags are generated from these entries
 * via `flag-factory.ts`; analysis syncs them into Postgres.
 *
 * @module lib/experiments/registry
 */

import type { ExperimentDefinition } from "./types"

export type {
  ExperimentDefinition,
  ExperimentKind,
  ExperimentMetric,
  ExperimentStatus,
  SurfaceScope,
} from "./types"

/**
 * Active and planned experiments. Add new entries here when shipping a test.
 */
export const EXPERIMENT_REGISTRY: readonly ExperimentDefinition[] = [
  {
    key: "homepage-hero-cta",
    name: "Homepage hero primary CTA label",
    kind: "copy",
    hypothesis:
      "Outcome-oriented CTA copy ('Optimize Your Ads') increases request-access clicks vs. the status-quo 'Request Access' label. Copy must not imply a free tier or early-access waitlist.",
    variants: ["control", "outcome_focused"],
    primaryMetric: "sign_up_click",
    guardrailMetrics: ["scroll_depth"],
    surface: { type: "path", path: "/" },
    status: "paused",
    minExposuresPerVariant: 500,
    minDays: 14,
  },
  {
    key: "footer-layout",
    name: "Global footer layout",
    kind: "component",
    hypothesis:
      "A flat-column footer (PR #228's researched IA: Stripe/Vercel-style flat link columns on desktop, a single-open accordion instead of a 2-column squeeze on mobile, Request Access as a Company text link rather than a competing CTA) reduces scroll fatigue and mobile mis-taps while maintaining framework discovery clicks.",
    variants: ["control", "compact"],
    primaryMetric: "footer_link_click",
    guardrailMetrics: ["scroll_depth"],
    surface: { type: "global" },
    // Concluded 2026-07-08 without a winner: superseded by the owner-approved
    // curated-footer redesign (the primary metric was hopelessly underpowered
    // at current traffic — ~43k exposures/arm for a 20% lift at a 1% base CTR).
    // Full wind-down record: docs/experiments/README.md § Concluded experiments.
    status: "concluded",
    minExposuresPerVariant: 1000,
    minDays: 21,
  },
] as const

// NOTE: global-surface experiments (e.g. the footer in the root layout) MAY run,
// but they must resolve their variant CLIENT-side — the control renders in static
// SSR HTML and swaps after hydration (see the retired `footer-layout` swap,
// `FooterExperimentSwap.client.tsx` in git history, mirroring the hero
// CTA). Resolving a global experiment on the SERVER render path would opt every
// route under the root layout into dynamic rendering; that footgun is guarded at
// the danger site — `getExperimentVariant` in `./server` throws for a `global`
// surface — rather than by a blanket "no global may run" rule here. The prior
// module-load guard existed only until the client-side swap landed.

/**
 * Running experiments only — used for flag evaluation exports.
 */
export const ACTIVE_EXPERIMENT_REGISTRY = EXPERIMENT_REGISTRY.filter(
  (e) => e.status === "running" || e.status === "draft",
)

/**
 * Looks up experiment metadata by flag key.
 *
 * @param {string} key - Experiment / flag key.
 * @returns {ExperimentDefinition | undefined} Definition when registered.
 */
export function getExperimentDefinition(
  key: string,
): ExperimentDefinition | undefined {
  return EXPERIMENT_REGISTRY.find((e) => e.key === key)
}
